import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Stepper, Step, StepLabel, IconButton, Input } from "@mui/material";
import { Grid, Paper } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Check from "@mui/icons-material/Check";
import WorkIcon from "@mui/icons-material/Work";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CategoryIcon from "@mui/icons-material/Category";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NoteIcon from "@mui/icons-material/Note";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import MDButton from "components/MDButton";
import {
  ColorlibStepIconRoot,
  ColorlibConnector,
} from "components/CommonComponent/CommonFunction";
import { getCardStyle } from "components/CommonComponent/CommonFunction";
import { useDispatch, useSelector } from "react-redux";
import { getProductList } from "actions/product";
import { productList } from "constants/product";
import { getProjectList } from "actions/project";
import { projectList } from "constants/project";
import { getUnitListing } from "actions/unit";
import { unitList } from "constants/unit";
import { stationList } from "constants/station";
import { getStationList } from "actions/station";
import { subActivityList } from "constants/subActivity";
import { getSubActivityList } from "actions/subActivity";
import { getMicroActivityList } from "actions/microActivity";
import { microActivityList } from "constants/microActivity";
import { getRemarkList } from "actions/remark";
import { remarkList } from "constants/remark";
import { hoursData, minutesData, activityTypes } from "components/staticData";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import MDInput from "components/MDInput";
import { activityTypeOptions } from "components/common/constant";
const STORAGE_KEY = "timesheet_draft";

function ColorlibStepIcon(props) {
  const { active, completed, className, icon, hasValue } = props;

  const icons = {
    1: <Inventory2Icon />,
    2: <WorkIcon />,
    3: <PrecisionManufacturingIcon />,
    4: <LocationOnIcon />,
    5: <CategoryIcon />,
    6: <SettingsSuggestIcon />,
    7: <AccessTimeIcon />,
    8: <NoteIcon />,
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ active, completed, hasValue }}
      className={className}
    >
      {active && hasValue ? (
        icons[String(icon)]
      ) : completed ? (
        <Check />
      ) : (
        icons[String(icon)]
      )}
    </ColorlibStepIconRoot>
  );
}
ColorlibStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
  className: PropTypes.string,
  icon: PropTypes.node,
  hasValue: PropTypes.bool,
};

const initialFields = {
  productType: { value: null },
  projectNumber: { value: null },
  unitNo: { value: null },
  subUnit: { value: null },
  stationName: { value: null },
  subActivity: { value: null },
  microActivity: { value: null },
  hours: { value: null },
  minutes: { value: null },
  remark: { value: "" },
  activityType: { value: null },
  remarkActivityType: { value: null },
};

const steps = [
  "Product",
  "Project",
  "Unit",
  "Station",
  "Sub-Activity",
  "Micro-Activity",
  "Hours",
  "Remark",
];

export default function TimeSheetStepper({
  onClose,
  onSubmit,
  rowData,
  isEditMode,
  remainingMinutes = 0,
  isOvertimeDuty = false,
}) {
  const { notify } = useNotify();
  const user = JSON.parse(localStorage.getItem("pt") || "{}");
  const userDeptId = user?.department?.ID;

  const dispatch = useDispatch();
  //product
  const productListData = useSelector((state) => state.product.productList);
  const unitListingData = useSelector((state) => state.unit.unitList);
  //project
  const projectListData = useSelector((state) => state.project.projectList);
  //station data
  const stationListData = useSelector((state) => state.station.stationList);
  //sub and micro
  const microActivityData = useSelector(
    (state) => state.microActivity.microActivityList,
  );
  const subActivityData = useSelector(
    (state) => state.subActivity.subActivityList,
  );
  //reamrk
  const remarkListData = useSelector((state) => state.remark.remarkList);

  const subActivitiesList = subActivityData?.data?.subActivities || [];
  const microActivitesList = microActivityData?.data?.microActivities || [];
  const products = productListData?.data?.Product || [];
  const projects = projectListData?.data?.project || [];
  const stations = stationListData?.data?.station || [];
  const remarkslist = remarkListData?.data?.remarks || [];

  const [timeSheetData, setTimeSheetData] = useState(initialFields);
  const [activeStep, setActiveStep] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [projectSearch, setProjectSearch] = useState("");

  useEffect(() => {
    getProductList(dispatch);
    return () => {
      dispatch({ type: productList, payload: {} });
      dispatch({ type: projectList, payload: {} });
      dispatch({ type: unitList, payload: {} });
      dispatch({ type: stationList, payload: {} });
      dispatch({ type: subActivityList, payload: {} });
      dispatch({ type: microActivityList, payload: {} });
      dispatch({ type: remarkList, payload: {} });
    };
  }, [dispatch]);

  // Initialize stepper fields if rowData is provided
  useEffect(() => {
    if (rowData) {
      let h = null,
        m = null;
      if (rowData.hours) {
        // rowData.hours could be "02:35" or number
        if (typeof rowData.hours === "string" && rowData.hours.includes(":")) {
          const parts = rowData.hours.split(":");
          h = parseInt(parts[0], 10);
          m = parseInt(parts[1], 10);
        } else {
          h = Number(rowData.hours);
          m = Number(rowData.minutes || 0);
        }
      }
      setTimeSheetData({
        productType: { value: rowData.productType || null },
        projectNumber: { value: rowData.projectNumber || null },
        unitNo: { value: rowData.unitNo || null },
        subUnit: { value: rowData.subUnit || null },
        stationName: { value: rowData.stationName || null },

        subActivity: { value: rowData.subActivity || null },
        microActivity: { value: rowData.microActivity || null },

        hours: { value: h },
        minutes: { value: m },

        remark: {
          value:
            typeof rowData?.remark === "object"
              ? rowData?.remark?.remark || ""
              : rowData?.remark || "",
        },
        activityType: { value: rowData.activityType || "" },
        remarkActivityType: { value: rowData.activityType || "" },
      });
      setActiveStep(0);
    }
  }, [rowData]);

  const computeActiveStepFromData = (data) => {
    const mergedData = {
      ...initialFields,
      ...data,
    };
    const stepCompletion = [
      !!mergedData.productType?.value,
      !!mergedData.projectNumber?.value,
      !!mergedData.unitNo?.value,
      !!mergedData.stationName?.value,
      !!mergedData.subActivity?.value,
      !!mergedData.microActivity?.value,
      mergedData.hours?.value != null || mergedData.minutes?.value != null,
      !!mergedData.remark?.value,
    ];

    const firstIncomplete = stepCompletion.findIndex((completed) => !completed);
    return firstIncomplete === -1 ? 7 : firstIncomplete;
  };

  useEffect(() => {
    if (!rowData && !isEditMode) {
      setTimeSheetData(initialFields);
      setActiveStep(0);
    }
  }, [rowData, isEditMode]);

  useEffect(() => {
    if (isEditMode) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    const savedDraft = localStorage.getItem(STORAGE_KEY);
    const parsedData = JSON.parse(savedDraft);

    if (savedDraft && parsedData?.employeeId === user?.employeeId) {
      const parsed = JSON.parse(savedDraft);

      const merged = Object.keys(initialFields).reduce((acc, key) => {
        const rawValue = parsed[key];
        if (rawValue && typeof rawValue === "object" && "value" in rawValue) {
          acc[key] = rawValue;
        } else if (rawValue !== undefined) {
          acc[key] = { value: rawValue };
        } else {
          acc[key] = initialFields[key];
        }
        return acc;
      }, {});

      setTimeSheetData(merged);
      setActiveStep(computeActiveStepFromData(merged));
    } else {
      setTimeSheetData(initialFields);
      setActiveStep(0);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (userDeptId) {
      getStationList(
        dispatch,
        null,
        JSON.stringify([
          {
            field: "DeptID",
            condition: "eq",
            filterValues: [String(userDeptId)],
          },
        ]),
      );
    }
  }, [userDeptId]);

  useEffect(() => {
    if (isEditMode && rowData) {
      if (rowData.productType?.ID) {
        getProjectList(
          dispatch,
          null,
          JSON.stringify([
            {
              field: "ProductID",
              condition: "eq",
              filterValues: [String(rowData.productType.ID)],
            },
          ]),
        );
      }

      if (rowData.projectNumber?.ID) {
        getUnitListing(
          dispatch,
          null,
          JSON.stringify([
            {
              field: "ProjectID",
              condition: "eq",
              filterValues: [String(rowData.projectNumber.ID)],
            },
          ]),
        );
      }

      if (rowData.stationName?.ID) {
        getSubActivityList(
          dispatch,
          null,
          JSON.stringify([
            {
              field: "StationID",
              condition: "eq",
              filterValues: [String(rowData.stationName.ID)],
            },
          ]),
        );
      }
      if (rowData.subActivity?.ID) {
        getMicroActivityList(
          dispatch,
          null,
          JSON.stringify([
            {
              field: "SubActivityID",
              condition: "eq",
              filterValues: [String(rowData.subActivity.ID)],
            },
          ]),
        );
      }
      getRemarkList(dispatch);
    }
  }, [isEditMode, rowData]);

  const handleNext = () => {
    // if (!canAccessStep(activeStep + 1)) return;
    setAnimate(false);
    setTimeout(() => {
      setActiveStep((prev) => prev + 1);
      setAnimate(true);
    }, 300);
  };

  const handleBack = () => {
    setAnimate(false);
    setTimeout(() => {
      setActiveStep((prev) => prev - 1);
      setAnimate(true);
    }, 300);
  };

  const stepValidation = {
    1: () => !!timeSheetData.productType.value,
    2: () => !!timeSheetData.projectNumber.value,
    3: () => !!timeSheetData.unitNo.value,
    4: () => !!timeSheetData.stationName.value,
    5: () => !!timeSheetData.subActivity.value,
    6: () => !!timeSheetData.microActivity.value,
  };
  const stepErrorMessage = {
    1: "Please select Product type first",
    2: "Please select Project first",
    3: "Please select Unit first",
    4: "Please select Station first",
    5: "Please select Sub Activity first",
    6: "Please select Micro Activity first",
  };
  const canAccessStep = (step) => {
    if (!stepValidation[step]) return true;
    return stepValidation[step]();
  };

  const handleAddMore = () => {
    const enteredMinutes =
      (timeSheetData.hours.value || 0) * 60 +
      (timeSheetData.minutes.value || 0);

    if (!isOvertimeDuty && !isEditMode && enteredMinutes > remainingMinutes) {
      const exceededMinutes = enteredMinutes - remainingMinutes;

      const exHr = Math.floor(exceededMinutes / 60);
      const exMin = exceededMinutes % 60;

      const maxHr = Math.floor(remainingMinutes / 60);
      const maxMin = remainingMinutes % 60;

      notify(
        `Time limit exceeded by ${exHr} hr ${exMin} min. Maximum allowed is ${maxHr} hr ${maxMin} min.`,
        "warning",
      );

      return;
    }

    const payload = {
      productType: timeSheetData.productType.value,
      projectNumber: timeSheetData.projectNumber.value,
      unitNo: timeSheetData.unitNo.value,
      subUnit: timeSheetData.subUnit.value,
      stationName: timeSheetData.stationName.value,
      subActivity: timeSheetData.subActivity.value,
      microActivity: timeSheetData.microActivity.value,
      hours: timeSheetData.hours.value,
      minutes: timeSheetData.minutes.value || 0,
      remark: timeSheetData.remark.value,
      activityType: timeSheetData.remarkActivityType.value,
    };
    onSubmit && onSubmit(payload);
    localStorage.removeItem(STORAGE_KEY);

    // reset
    setTimeSheetData({
      ...timeSheetData,
      subActivity: { value: null },
      microActivity: { value: null },
      hours: { value: null },
      minutes: { value: null },
      remark: { value: "" },
      activityType: { value: null },
      remarkActivityType: { value: null },
    });

    setActiveStep(4);
  };

  const renderStep = (step) => {
    if (!canAccessStep(step)) {
      return (
        <MDTypography textAlign="center" color="error">
          {stepErrorMessage[step]}
        </MDTypography>
      );
    }

    switch (step) {
      case 0:
        return renderProductTypeStep();
      case 1:
        return renderProjectStep();
      case 2:
        return renderUnitStep();
      case 3:
        return renderStationStep();
      case 4:
        return renderSubActivityStep();
      case 5:
        return renderMicroActivityStep();
      case 6:
        return renderHoursStep();
      case 7:
        return renderRemarkStep();
      default:
        return null;
    }
  };

  const renderNoData = (label = "No data available") => (
    <MDBox textAlign="center" py={3}>
      <MDTypography color="text">{label}</MDTypography>
    </MDBox>
  );

  const renderProductTypeStep = () => {
    const activeProducts = products.filter((p) => p.status === "ACTIVE");

    if (activeProducts.length === 0) {
      return renderNoData("No products available");
    }

    return (
      <MDBox>
        <MDTypography fontWeight="bold" mb={2} pl={2}>
          Select Product Type
        </MDTypography>

        <MDBox sx={{ maxHeight: "340px", overflowY: "auto", pr: 1 }}>
          <Grid container spacing={2} mt={1} pl={2}>
            {activeProducts.map((product) => (
              <Grid item xs={12} sm={6} md={3} key={product.ID}>
                <Paper
                  onClick={() => {
                    handleChange("productType", product);
                    handleNext();
                  }}
                  sx={getCardStyle(
                    timeSheetData.productType.value?.ID === product.ID,
                    0,
                  )}
                >
                  <MDTypography fontWeight="bold">{product.type}</MDTypography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      </MDBox>
    );
  };

  const fetchProjects = (productId, projectNo = "") => {
    if (!productId) return;

    const filterParams = [
      {
        field: "ProductID",
        condition: "eq",
        filterValues: [String(productId)],
      },
    ];

    if (projectNo.trim()) {
      filterParams.push({
        field: "ProjectNo",
        condition: "like",
        filterValues: [projectNo.trim()],
      });
    }

    getProjectList(dispatch, null, JSON.stringify(filterParams));
  };
  //function to select project details
  const renderProjectStep = () => {
    const activeProjects = projects.filter((p) => p.status === "ACTIVE");

    return (
      <MDBox>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          px={2}
        >
          <MDTypography fontWeight="bold">Select Project</MDTypography>

          <MDInput
            type="text"
            placeholder="Search by project number"
            value={projectSearch}
            onChange={(e) => {
              const value = e.target.value;
              setProjectSearch(value);
              fetchProjects(timeSheetData.productType.value?.ID, value);
            }}
            sx={{
              width: "280px",
            }}
          />
        </MDBox>

        {activeProjects.length === 0 ? (
          renderNoData("No projects available")
        ) : (
          <MDBox sx={{ maxHeight: "340px", overflowY: "auto", pr: 1 }}>
            <Grid container spacing={2} mt={1} pl={2}>
              {activeProjects.map((project) => (
                <Grid item xs={12} sm={6} md={3} key={project.ID}>
                  <Paper
                    onClick={() => {
                      handleChange("projectNumber", project);
                      handleNext();
                    }}
                    sx={getCardStyle(
                      timeSheetData.projectNumber.value?.ID === project.ID,
                      1,
                    )}
                  >
                    <MDTypography fontWeight="bold">
                      {project.projectNo}
                    </MDTypography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </MDBox>
        )}
      </MDBox>
    );
  };

  //function to select unit no based on selected project no
  const renderUnitStep = () => {
    const selectedProject = timeSheetData.projectNumber.value;
    if (!selectedProject) {
      return renderNoData("Please select project first");
    }
    const filteredUnits =
      selectedProject?.units?.filter((u) => u.status === "ACTIVE") || [];
    const filterSubUnits = selectedProject?.subUnits || [];

    if (filteredUnits.length === 0 && filterSubUnits.length === 0) {
      return renderNoData("No units available");
    }
    const unitSelected = !!timeSheetData.unitNo.value; //check unit option is selected or not

    return (
      <MDBox>
        <MDTypography fontWeight="bold" mb={2} pl={2}>
          Select Unit No
        </MDTypography>

        <MDBox sx={{ maxHeight: "340px", overflowY: "auto", pr: 1 }}>
          <Grid container spacing={2} mt={1} pl={2}>
            {filteredUnits.map((unit) => (
              <Grid item key={unit.ID}>
                <Paper
                  onClick={() => handleChange("unitNo", unit)}
                  sx={getCardStyle(
                    timeSheetData.unitNo.value?.ID === unit.ID,
                    2,
                  )}
                >
                  <MDTypography fontWeight="bold">{unit.unitNo}</MDTypography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </MDBox>

        {/* // if unit is selected then able to select the sub unit */}
        {filterSubUnits.length > 0 && (
          <MDBox
            mt={3}
            pl={2}
            sx={{
              opacity: unitSelected ? 1 : 0.4,
              pointerEvents: unitSelected ? "auto" : "none",
            }}
          >
            <MDTypography fontWeight="bold" mb={2}>
              Select Sub Unit
            </MDTypography>

            <Grid container spacing={2}>
              {filterSubUnits.map((subUnit, index) => (
                <Grid item key={index}>
                  <Paper
                    onClick={() => {
                      handleChange("subUnit", subUnit);
                      handleNext();
                    }}
                    sx={getCardStyle(
                      timeSheetData.subUnit?.value === subUnit,
                      2,
                    )}
                  >
                    <MDTypography fontWeight="bold">{subUnit}</MDTypography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </MDBox>
        )}
      </MDBox>
    );
  };

  //function to select station no
  const renderStationStep = () => {
    const filteredStations = stations.filter((s) => s.status === "ACTIVE");
    if (filteredStations.length === 0) {
      return renderNoData("No stations available for this department");
    }

    return (
      <MDBox>
        <MDTypography fontWeight="bold" mb={2} pl={2}>
          Select Station
        </MDTypography>

        <MDBox sx={{ maxHeight: "340px", overflowY: "auto", pr: 1 }}>
          <Grid container spacing={2} mt={1} pl={2}>
            {filteredStations.map((station) => (
              <Grid item xs={12} sm={6} md={2} key={station.ID}>
                <Paper
                  onClick={() => {
                    handleChange("stationName", station);
                    handleNext();
                  }}
                  sx={getCardStyle(
                    timeSheetData.stationName.value?.ID === station.ID,
                    3,
                  )}
                >
                  <MDTypography fontWeight="bold">{station.name}</MDTypography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      </MDBox>
    );
  };

  const renderSubActivityStep = () => {
    const activeSubActivities = subActivitiesList.filter((a) => {
      if (a.status !== "ACTIVE") return false;

      if (!timeSheetData.activityType?.value) return true;

      return a.activityType === timeSheetData.activityType.value;
    });

    const isActivityTypeSelected = !!timeSheetData.activityType?.value;

    return (
      <MDBox>
        <MDBox
          display="flex"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
          pl={2}
          mb={4}
        >
          <MDTypography fontWeight="bold">Select Activity Type</MDTypography>

          <Grid container spacing={2} sx={{ flex: 1 }}>
            {activityTypeOptions.map((type) => (
              <Grid item xs={6} sm={3} md={2} key={type.value}>
                <Paper
                  onClick={() => handleChange("activityType", type.value)}
                  sx={{
                    ...getCardStyle(
                      timeSheetData.activityType?.value === type.value,
                      4,
                    ),
                    textAlign: "center",
                    py: 1.5,
                  }}
                >
                  <MDTypography fontWeight="bold">{type.label}</MDTypography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </MDBox>

        <MDBox sx={{ opacity: isActivityTypeSelected ? 1 : 0.4 }}>
          <MDTypography fontWeight="bold" mb={1} pl={2}>
            Select Sub Activity
          </MDTypography>

          <MDBox
            sx={{
              maxHeight: "340px",
              overflowY: "auto",
              pr: 1,
              pointerEvents: isActivityTypeSelected ? "auto" : "none",
            }}
          >
            {activeSubActivities.length === 0 ? (
              renderNoData("No sub activities available")
            ) : (
              <Grid container spacing={2} mt={1} pl={2}>
                {activeSubActivities.map((sub) => (
                  <Grid item xs={12} sm={6} md={4} key={sub.ID}>
                    <Paper
                      onClick={() => {
                        handleChange("subActivity", sub);
                        handleChange("microActivity", null);
                        handleNext();
                      }}
                      sx={getCardStyle(
                        timeSheetData.subActivity.value?.ID === sub.ID,
                        4,
                      )}
                    >
                      <MDTypography fontWeight="bold">{sub.name}</MDTypography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </MDBox>
        </MDBox>
      </MDBox>
    );
  };

  const renderMicroActivityStep = () => {
    const filteredMicro = microActivitesList.filter(
      (m) => m.status === "ACTIVE",
    );

    if (filteredMicro.length === 0) {
      return renderNoData("No micro activities available");
    }

    return (
      <MDBox>
        <MDTypography fontWeight="bold" mb={2} pl={2}>
          Select Micro Activity
        </MDTypography>

        <MDBox sx={{ maxHeight: "340px", overflowY: "auto", pr: 1 }}>
          <Grid container spacing={2} mt={1} pl={2}>
            {filteredMicro.map((micro) => (
              <Grid item xs={12} sm={6} md={4} key={micro.ID}>
                <Paper
                  onClick={() => {
                    handleChange("microActivity", micro);
                    handleNext();
                  }}
                  sx={getCardStyle(
                    timeSheetData.microActivity.value?.ID === micro.ID,
                    5,
                  )}
                >
                  <MDTypography fontWeight="bold">{micro.name}</MDTypography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      </MDBox>
    );
  };

  const renderHoursStep = () => {
    const { hours, minutes } = timeSheetData;
    const selectedHours = hours.value;
    const selectedMinutes = minutes.value;

    const getTotalTimeLabel = () => {
      const filledHours = selectedHours ?? 0;
      const filledMinutes = selectedMinutes ?? 0;

      if (selectedHours === null && selectedMinutes === null) {
        return "Selected Hours: 0 hr 0 min";
      }

      if (selectedMinutes !== null || selectedHours !== null) {
        return `Selected Hours: ${filledHours} hr ${filledMinutes} min`;
      }

      return "Selected Hours: 0 hr 0 min";
    };

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography fontWeight="bold">Select Time</MDTypography>
            <MDTypography variant="h5">{getTotalTimeLabel()}</MDTypography>
          </MDBox>
        </Grid>

        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={7.4}>
              <MDTypography mb={2} fontWeight="bold" pl={2}>
                Hours
              </MDTypography>

              <Grid container spacing={2}>
                {hoursData.map((h) => (
                  <Grid item xs={6} sm={4} md={4} key={h.value}>
                    <Paper
                      onClick={() => {
                        handleChange("hours", h.value);
                        if (h.value === 8) {
                          handleNext();
                        }
                      }}
                      sx={{
                        ...getCardStyle(selectedHours === h.value, 6),
                        textAlign: "center",
                        py: 1.5,
                      }}
                    >
                      <MDTypography fontWeight="bold">{h.label}</MDTypography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={0.2}></Grid>
            <Grid item xs={0.4} display="flex" justifyContent="center">
              <MDBox
                sx={{
                  width: "0.313rem",
                  backgroundColor: "#ccc",
                  height: "100%",
                }}
              />
            </Grid>
            <Grid item xs={0.2}></Grid>
            <Grid item xs={3.8}>
              <MDTypography mb={2} fontWeight="bold">
                Minutes
              </MDTypography>

              <Grid container spacing={2}>
                {minutesData.map((m) => (
                  <Grid item xs={6} sm={6} md={6} key={m.value}>
                    <Paper
                      onClick={() => {
                        handleChange("minutes", m.value);
                        handleNext();
                      }}
                      sx={{
                        ...getCardStyle(selectedMinutes === m.value, 6),
                        textAlign: "center",
                        py: 1.5,
                      }}
                    >
                      <MDTypography fontWeight="bold">{m.label}</MDTypography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderRemarkStep = () => {
    const activeRemarks = remarkslist.filter((r) => r.status === "ACTIVE");

    if (activeRemarks.length === 0) {
      return renderNoData("No remarks available");
    }

    const isRemarkActivityTypeSelected =
      !!timeSheetData.remarkActivityType?.value;

    // only string value
    const selectedRemark =
      typeof timeSheetData?.remark?.value === "string"
        ? timeSheetData.remark.value
        : "";

    return (
      <>
        <MDBox pl={2} mb={3}>
          <MDTypography fontWeight="bold" mb={2}>
            Activity Type
          </MDTypography>

          <Grid container spacing={2}>
            {activityTypes.map((type) => (
              <Grid item xs={6} sm={4} md={3} key={type.value}>
                <Paper
                  onClick={() => handleChange("remarkActivityType", type.value)}
                  sx={getCardStyle(
                    timeSheetData.remarkActivityType?.value === type.value,
                    7,
                  )}
                >
                  <MDTypography fontWeight="bold">{type.label}</MDTypography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </MDBox>

        <MDBox
          mt={3}
          pl={2}
          sx={{
            opacity: isRemarkActivityTypeSelected ? 1 : 0.4,
            pointerEvents: isRemarkActivityTypeSelected ? "auto" : "none",
          }}
        >
          <MDTypography fontWeight="bold" mb={1} pl={2}>
            Select Remark
          </MDTypography>

          <MDBox
            sx={{
              maxHeight: "340px",
              overflowY: "auto",
              pr: 1,
              pointerEvents: isRemarkActivityTypeSelected ? "auto" : "none",
            }}
          >
            <Grid container spacing={2} mt={1} pl={2}>
              {activeRemarks.map((reason) => (
                <Grid item xs={12} sm={6} md={3} key={reason.ID}>
                  <Paper
                    onClick={() => handleChange("remark", reason.remark)}
                    sx={getCardStyle(
                      selectedRemark === reason.remark?.trim(),
                      7,
                    )}
                  >
                    <MDTypography fontWeight="bold">
                      {reason.remark}
                    </MDTypography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </MDBox>

          <MDBox mt={2} mb={2} pl={2}>
            <MDInput
              fullWidth
              rows={2}
              placeholder="Enter custom remark..."
              inputProps={{ maxLength: 100 }}
              value={selectedRemark || ""}
              onChange={(e) => handleChange("remark", e.target.value)}
              disabled={!isRemarkActivityTypeSelected}
              sx={{
                "& textarea": {
                  fontSize: "1.25rem",
                  fontWeight: "600",
                },
              }}
            />
          </MDBox>
        </MDBox>
      </>
    );
  };
  const handleChange = (key, value) => {
    setTimeSheetData((prev) => {
      const updated = {
        ...prev,
        [key]: { value },
        ...(key === "projectNumber" && {
          unitNo: { value: null },
          subUnit: { value: null },
        }),
        ...(key === "unitNo" && {
          subUnit: { value: null },
        }),
        ...(key === "hours" && { minutes: { value: null } }),
        ...(key === "minutes" &&
        (prev.hours?.value === null || prev.hours?.value === undefined)
          ? { hours: { value: 0 } }
          : {}),
      };
      if (key === "productType" && value?.ID) {
        setProjectSearch("");
        fetchProjects(value.ID);
      }
      if (key === "projectNumber" && value?.ID) {
        getUnitListing(
          dispatch,
          null,
          JSON.stringify({
            projectId: value.ID,
          }),
        );
      }

      if (key === "stationName" && value?.ID) {
        getSubActivityList(
          dispatch,
          null,
          JSON.stringify([
            {
              field: "StationID",
              condition: "eq",
              filterValues: [String(value.ID)],
            },
          ]),
        );
      }
      if (key === "subActivity" && value?.ID) {
        getMicroActivityList(
          dispatch,
          null,
          JSON.stringify([
            {
              field: "SubActivityID",
              condition: "eq",
              filterValues: [String(value.ID)],
            },
          ]),
        );
      }
      if (key === "microActivity" && value?.ID) {
        getRemarkList(dispatch);
      }
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...updated,
          // entries: parsed.entries || [],
          employeeId: user?.employeeId || null,
        }),
      );

      return updated;
    });
  };

  return (
    <MDBox px={2} py={1}>
      <MDBox position="relative" mb={3}>
        <MDTypography variant="h4" textAlign="center">
          {isEditMode ? "Edit Timesheet" : "Add Timesheet"}
        </MDTypography>

        <MDBox
          position="absolute"
          top={0}
          right={0}
          display="flex"
          alignItems="center"
          gap={1}
        >
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </MDBox>
      </MDBox>

      <Stepper
        alternativeLabel
        activeStep={activeStep}
        connector={<ColorlibConnector />}
        sx={{
          background: "transparent !important",
          boxShadow: "none !important",
          p: "0 !important",
          pt: "0.5rem !important",
          mb: "1rem !important",
        }}
      >
        {steps.map((label, index) => {
          const getStepValue = () => {
            switch (index) {
              case 0:
                return timeSheetData.productType?.value?.type;
              case 1:
                return timeSheetData.projectNumber?.value?.projectNo;
              case 2: {
                const unit = timeSheetData.unitNo?.value?.unitNo;
                const subUnit = timeSheetData.subUnit?.value;
                if (!unit) return null;
                return (
                  <>
                    {unit}
                    {subUnit && (
                      <>
                        <br />
                        {subUnit}
                      </>
                    )}
                  </>
                );
              }
              case 3:
                return timeSheetData.stationName?.value?.name;
              case 4:
                return timeSheetData.subActivity?.value?.name;
              case 5:
                return timeSheetData.microActivity?.value?.name;
              case 6:
                const hoursValue = timeSheetData.hours?.value ?? null;
                const minutesValue = timeSheetData.minutes?.value ?? null;

                if (hoursValue !== null || minutesValue !== null) {
                  return `${hoursValue ?? 0} hr ${minutesValue ?? 0} min`;
                }
                return null;
              case 7:
                return timeSheetData.remark?.value
                  ? `${timeSheetData.remarkActivityType?.value} - ${timeSheetData.remark?.value}`
                  : null;
              default:
                return null;
            }
          };

          const value = getStepValue();

          return (
            <Step
              key={label}
              completed={!!getStepValue()} // mark completed if value exists
            >
              <StepLabel
                StepIconComponent={(props) => (
                  <ColorlibStepIcon
                    {...props}
                    hasValue={!!value} //add this
                  />
                )}
                onClick={() => setActiveStep(index)}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    cursor: "pointer",
                  },
                }}
              >
                <MDBox textAlign="center">
                  <MDTypography variant="body1">{label}</MDTypography>

                  {value && (
                    <MDTypography
                      variant="h6"
                      color="text"
                      sx={{
                        fontWeight: 500,
                        mt: "1rem !important",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {value}
                    </MDTypography>
                  )}
                </MDBox>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <MDBox
        key={activeStep}
        mt={1}
        sx={{
          transition: "all 0.3s ease",
          opacity: animate ? 1 : 0,
          transform: animate ? "translateX(0)" : "translateX(20px)",
        }}
      >
        {renderStep(activeStep)}
      </MDBox>

      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={4}
        sx={{ minHeight: "60px" }}
      >
        {activeStep !== 0 ? (
          <MDButton variant="gradient" color="light" onClick={handleBack}>
            Back
          </MDButton>
        ) : (
          <span />
        )}

        <MDBox>
          {(isEditMode || activeStep >= 7) && (
            <MDButton
              variant="contained"
              color="deepBlue"
              onClick={handleAddMore}
              disabled={!timeSheetData.remark?.value?.trim()}
              sx={{ px: 2, py: 1, fontSize: "1rem" }}
            >
              {isEditMode ? "Save Log Hour" : "Add Log Hour"}
            </MDButton>
          )}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

TimeSheetStepper.propTypes = {
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  rowData: PropTypes.shape({
    id: PropTypes.number,
    productType: PropTypes.object,
    projectNumber: PropTypes.object,
    unitNo: PropTypes.object,
    subUnit: PropTypes.string,
    stationName: PropTypes.object,
    subActivity: PropTypes.string,
    microActivity: PropTypes.string,
    hours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minutes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    remark: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        remark: PropTypes.string,
      }),
    ]),
    activityType: PropTypes.string,
  }),
  isEditMode: PropTypes.bool,
  remainingMinutes: PropTypes.number,
  isOvertimeDuty: PropTypes.bool,
};
